/**
 * Client Hooks
 * React Query hooks for client data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientsApi } from '@/api'
import type {
  ClientCreate,
  ClientUpdate,
  ClientFilters,
} from '@/types/client'
import type { PaginationParams } from '@/types/common'

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (filters?: ClientFilters, pagination?: PaginationParams) =>
    [...clientKeys.lists(), { filters, pagination }] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  cases: (id: string) => [...clientKeys.detail(id), 'cases'] as const,
  visits: (id: string) => [...clientKeys.detail(id), 'visits'] as const,
}

/**
 * Get paginated list of clients
 */
export function useClients(
  filters?: ClientFilters,
  pagination?: PaginationParams
) {
  return useQuery({
    queryKey: clientKeys.list(filters, pagination),
    queryFn: () => clientsApi.getAll(filters, pagination),
  })
}

/**
 * Get single client by ID
 */
export function useClient(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientsApi.getById(id),
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
  })
}

/**
 * Get client's cases
 */
export function useClientCases(id: string) {
  return useQuery({
    queryKey: clientKeys.cases(id),
    queryFn: () => clientsApi.getCases(id),
    enabled: !!id,
  })
}

/**
 * Get client's visits
 */
export function useClientVisits(id: string) {
  return useQuery({
    queryKey: clientKeys.visits(id),
    queryFn: () => clientsApi.getVisits(id),
    enabled: !!id,
  })
}

/**
 * Create new client
 */
export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ClientCreate) => clientsApi.create(data),
    onSuccess: () => {
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}

/**
 * Update existing client
 */
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClientUpdate }) =>
      clientsApi.update(id, data),
    onSuccess: (updatedClient) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: clientKeys.detail(updatedClient.id),
      })
    },
  })
}

/**
 * Delete client
 */
export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => clientsApi.delete(id),
    onSuccess: () => {
      // Invalidate clients list
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    },
  })
}
